import Box from 'assets/components/box/Box';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { HttpStatus } from 'config/Api.Config';
import { compact, isEmpty, uniq } from 'lodash';
import ClaimType from 'models/claims/claimType/Model.ClaimType';
import claimTypeDealerAutomationRuleDeleteApi from 'models/dealerManagement/claimTypeDealerAutomationRule/delete/Api.ClaimTypeDealerAutomationRule.Delete';
import AutomationRulesTable from 'module/automation/components/Automation.Rule.Table';
import { createRule } from 'module/automation/helpers/ruleHelpers';
import { createDealerClaimAutomationSchema } from 'module/automation/schemas/ClaimAutomationSchema';
import useClaimStatuses from 'module/claims/hooks/useClaimStatuses';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { DealerForm, useDealerProvider } from '../../Dealer.Hooks';

export default function DealerClaimTypeAutomationRules({
  additionalClaimTypeIds,
}: {
  additionalClaimTypeIds: number[];
}) {
  const { lang } = getLocales();
  const { form } = useModalForm<DealerForm>();
  const { selected } = useDealerProvider();
  const { automationClaimStatuses, automationClaimJobStatuses } = useClaimStatuses();

  const claimTypeIds = useMemo(() => {
    return uniq([...compact(selected?.dealerClaimTypes?.map((c) => c.claimTypeId)), ...additionalClaimTypeIds]);
  }, [selected?.dealerClaimTypes, additionalClaimTypeIds]);

  const { payload: claimTypesPayload } = useApi(
    {
      action: ClaimType.list,
      wait: !claimTypeIds.length,
      body: { id: claimTypeIds },
    },
    [claimTypeIds]
  );

  const createOverrideRuleData = (
    rules: Model.IClaimTypeDealerAutomationRule[],
    rule: Model.IClaimTypeAutomationRule,
    claimTypeId: number
  ) => ({
    id: Math.min(0, ...rules.map((item) => item.id || 0)) - 1,
    claimTypeId,
    claimTypeAutomationRuleId: rule.id,
  });

  const updateRules = (
    id: number,
    newRules: Model.IClaimTypeDealerAutomationRule[],
    claimTypeId: number
  ) => {
    const otherRules = form.data?.claimTypeDealerAutomationRules?.filter(
      (entry) => entry.claimTypeAutomationRuleId !== id
    );
    const updatedRules = newRules.map((entry) => ({
      ...entry,
      claimTypeId,
      claimTypeAutomationRuleId: id,
    }));

    form.update({ claimTypeDealerAutomationRules: [...(otherRules || []), ...updatedRules] });
  };

  const deleteRule = async (claimTypeRuleId: number, rule?: Model.IClaimTypeDealerAutomationRule) => {
    const updatedRules = form.data?.claimTypeDealerAutomationRules?.filter(
      (it) => it.claimTypeAutomationRuleId !== claimTypeRuleId || it.id !== rule?.id
    );
    form.update({ claimTypeDealerAutomationRules: updatedRules });

    if (!updatedRules?.some((it) => it.claimTypeAutomationRuleId === claimTypeRuleId)) {
      setOverrideClaimRule((prev) => ({
        ...prev,
        [claimTypeRuleId]: false,
      }));
    }

    if (rule?.id && rule.id > 0) {
      const res = await claimTypeDealerAutomationRuleDeleteApi({ id: rule.id });
      if (res.status !== HttpStatus.ok) {
        addLog({ error: res.message });
      }
    }
  };

  const deleteOverrideRules = async (claimTypeRuleId: number) => {
    const deletedRules =
      form.data?.claimTypeDealerAutomationRules?.filter((it) => it.claimTypeAutomationRuleId === claimTypeRuleId) ||
      [];
    const updatedRules = form.data?.claimTypeDealerAutomationRules?.filter(
      (it) => it.claimTypeAutomationRuleId !== claimTypeRuleId
    );
    form.update({ claimTypeDealerAutomationRules: updatedRules });

    setOverrideClaimRule((prev) => ({
      ...prev,
      [claimTypeRuleId]: false,
    }));

    for (const deletedRule of deletedRules) {
      if (deletedRule.id && deletedRule.id > 0) {
        const res = await claimTypeDealerAutomationRuleDeleteApi({ id: deletedRule.id });
        if (res.status !== HttpStatus.ok) {
          addLog({ error: res.message });
        }
      }
    }
  };

  const updateEnabled = (claimTypeId: number, newData: { isClaimTypeAutomationEnabled: any }) => {
    const currentDealerClaimTypes = [...(form.data.dealerClaimTypes || [])];
    const updated = currentDealerClaimTypes.map((entry) =>
      entry.claimTypeId === claimTypeId ? { ...entry, ...newData } : entry
    );
    form.update({ dealerClaimTypes: updated });
  };

  const [overrideClaimRule, setOverrideClaimRule] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isEmpty(overrideClaimRule)) {
      const initialOverrides: Record<string, boolean> = {};

      claimTypesPayload?.data?.forEach((claimType) => {
        const automationRuleIds = claimType.claimTypeAutomationRules?.map((r) => r.id) ?? [];
        const hasDealerOverride = (form.data?.claimTypeDealerAutomationRules ?? []).some((dealerRule) =>
          automationRuleIds.includes(dealerRule.claimTypeAutomationRuleId)
        );

        if (hasDealerOverride) {
          automationRuleIds.forEach((ruleId) => {
            if (
              (form.data?.claimTypeDealerAutomationRules ?? []).some(
                (dealerRule) => dealerRule.claimTypeAutomationRuleId === ruleId
              )
            ) {
              initialOverrides[ruleId] = true;
            }
          });
        }
      });

      setOverrideClaimRule(initialOverrides);
    }
  }, [claimTypesPayload?.data, form.data?.claimTypeDealerAutomationRules]);

  return (
    <FormWrapper>
      <DataMap
        data={claimTypesPayload?.data}
        render={({ data: claimType }) => {
          const dealerClaimType = form.data?.dealerClaimTypes?.find((it) => it.claimTypeId === claimType.id);
          const claimTypeSchema = createDealerClaimAutomationSchema({
            claimStatuses: automationClaimStatuses,
            claimJobStatuses: automationClaimJobStatuses,
            claimTypeDataPoints: claimType.claimTypeDataPoints,
          });

          return (
            !!claimType.claimTypeAutomationRules.length &&
            dealerClaimType && (
              <Fragment key={claimType.id}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: Theme.Size.M }}>
                    <h3>{claimType.name}</h3>
                    <CheckWrapper>
                      <CheckInput
                        name="isClaimTypeAutomationEnabled"
                        label={lang.enabled}
                        value={dealerClaimType?.isClaimTypeAutomationEnabled}
                        onChange={({ isClaimTypeAutomationEnabled }) =>
                          updateEnabled(claimType.id, { isClaimTypeAutomationEnabled })
                        }
                        class={inputStyles.check.switch}
                        htmlElementProps={{
                          title: dealerClaimType?.isClaimTypeAutomationEnabled ? lang.disable : lang.enable,
                        }}
                      />
                    </CheckWrapper>
                  </div>
                </div>
                &nbsp;
                {dealerClaimType?.isClaimTypeAutomationEnabled && (
                  <DataMap
                    data={claimType.claimTypeAutomationRules}
                    render={({ data: rule, index }) => {
                      const currentRules = form.data?.claimTypeDealerAutomationRules?.filter(
                        (c) => c.claimTypeAutomationRuleId === rule.id
                      ) || [];

                      return (
                        <div key={rule.id}>
                          <Box style={{ display: 'flex', flexDirection: 'column', marginBottom: '1rem' }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: Theme.Size.M,
                                marginBottom: '10px',
                              }}
                            >
                              <h3>{`${lang.claimRule} #${index + 1}`}</h3>
                              <CheckWrapper>
                                <CheckInput
                                  name="overrideClaimRule"
                                  label={lang.overrideClaimRule}
                                  value={overrideClaimRule[rule.id]}
                                  onChange={({ overrideClaimRule: isOverrided }) => {
                                    setOverrideClaimRule((prev) => ({ ...prev, [rule.id]: isOverrided }));
                                    const allOverrideRules = form.data?.claimTypeDealerAutomationRules || [];

                                    if (isOverrided) {
                                      const alreadyExists = allOverrideRules.some(
                                        (it) => it.claimTypeAutomationRuleId === rule.id
                                      );
                                      if (alreadyExists) return;

                                      const newEntry = createRule(
                                        claimTypeSchema,
                                        createOverrideRuleData([], rule, claimType.id)
                                      ) as Model.IClaimTypeDealerAutomationRule;

                                      form.update({
                                        claimTypeDealerAutomationRules: [...allOverrideRules, newEntry],
                                      });
                                    } else {
                                      deleteOverrideRules(rule.id);
                                    }
                                  }}
                                  class={inputStyles.check.radio}
                                />
                              </CheckWrapper>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: Theme.Size.M }}>
                              <AutomationRulesTable<Model.IClaimTypeAutomationRule>
                                value={[rule]}
                                schema={claimTypeSchema}
                                isReadOnlySummary
                              />
                            </div>
                          </Box>
                          {overrideClaimRule[rule.id] && (
                            <div>
                              <h3>{`${lang.overrideClaimRule} #${index + 1}`}</h3>
                              <AutomationRulesTable<Model.IClaimTypeDealerAutomationRule>
                                value={currentRules as Model.IClaimTypeDealerAutomationRule[]}
                                schema={claimTypeSchema}
                                createRuleData={(rules) => createOverrideRuleData(rules, rule, claimType.id)}
                                onChange={(rules) => updateRules(rule.id, rules, claimType.id)}
                                onDeleteRule={(_, overrideRule) => deleteRule(rule.id, overrideRule)}
                              />
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                )}
                <br />
              </Fragment>
            )
          );
        }}
      />
    </FormWrapper>
  );
}
